import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router';
import {
  BadgeCheck,
  CheckCircle2,
  ClipboardList,
  Clock3,
  Database,
  Flame,
  KeyRound,
  LockKeyhole,
  RotateCcw,
  Search,
  ShieldCheck,
  Trophy,
  Unlock,
  XCircle,
} from 'lucide-react';
import Eyebrow from '../components/ui/Eyebrow';
import HeroBackground from '../components/ui/HeroBackground';
import ManagerAvatar from '../components/ui/ManagerAvatar';
import SectionHeader from '../components/ui/SectionHeader';
import {
  createKeeperGauntletDeck,
  GAUNTLET_TARGET_STREAK,
  GAUNTLET_TIME_LIMIT_SECONDS,
  loadKeeperGauntletQuestionBank,
} from '../utils/keeperGauntlet';
import { keeperVaultStorageKey } from '../utils/keeperVault';
import { ACTIVE_MEMBERS } from '../utils/managers';
import { managerSlug } from '../utils/slug';
import { getKeeperDossier } from '../data/keeperDossiers';
import { Reveal } from '../motion';

const ADVANCE_DELAY_MS = 650;

const RESEARCH_STEPS = [
  {
    icon: ClipboardList,
    label: 'Candidate pool',
    copy: 'List every player from the captured Yahoo pre-draft roster source, including drafted players and waiver/post-draft pickups.',
  },
  {
    icon: KeyRound,
    label: 'Keeper cost',
    copy: 'Map drafted players to their original 2025 draft round and label players who went undrafted as no-pick keepers.',
  },
  {
    icon: Search,
    label: '2026 research',
    copy: 'Compare projections, ADP, expert ranks, role, injury news, schedule context, scoring fit, and two-QB value against the cost.',
  },
  {
    icon: ShieldCheck,
    label: 'Final verdict',
    copy: 'Give one best keeper, one value play, one safe play, one upside swing, and one trap to avoid, with sources attached.',
  },
];

const RULE_CARDS = [
  {
    icon: Flame,
    label: 'Unlock rule',
    value: `${GAUNTLET_TARGET_STREAK} straight`,
    copy: 'A wrong answer or timeout resets the streak to zero.',
  },
  {
    icon: Clock3,
    label: 'Clock',
    value: `${GAUNTLET_TIME_LIMIT_SECONDS} seconds`,
    copy: 'Every question is timed. Waiting out the clock counts as a miss.',
  },
  {
    icon: Database,
    label: 'Question pool',
    value: '200-ish receipts',
    copy: 'First-round picks, standings, champions, rivalries, team names, and headline weekly scores.',
  },
  {
    icon: LockKeyhole,
    label: 'Reward',
    value: 'Keeper edge',
    copy: 'Winning opens the research built to help choose next season\'s keeper.',
  },
];

function readInitialUnlocks() {
  if (typeof window === 'undefined') return {};

  return ACTIVE_MEMBERS.reduce((acc, manager) => {
    const slug = managerSlug(manager);
    acc[slug] = window.localStorage.getItem(keeperVaultStorageKey(slug)) === 'unlocked';
    return acc;
  }, {});
}

function managerUnlockSlug(manager) {
  return managerSlug(manager);
}

function difficultyClass(difficulty) {
  if (difficulty === 'sicko') return 'text-accent-red';
  if (difficulty === 'medium') return 'text-ink-tertiary';
  return 'text-accent-gold';
}

export default function KeeperVault() {
  const [selectedManager, setSelectedManager] = useState(ACTIVE_MEMBERS[0]);
  const [unlocked, setUnlocked] = useState(readInitialUnlocks);
  const [questionBank, setQuestionBank] = useState([]);
  const [loadingQuestions, setLoadingQuestions] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [deck, setDeck] = useState([]);
  const [questionIndex, setQuestionIndex] = useState(0);
  const [streak, setStreak] = useState(0);
  const [bestStreak, setBestStreak] = useState(0);
  const [timeLeft, setTimeLeft] = useState(GAUNTLET_TIME_LIMIT_SECONDS);
  const [isRunning, setIsRunning] = useState(false);
  const [answered, setAnswered] = useState(false);
  const [selectedChoice, setSelectedChoice] = useState('');
  const [feedback, setFeedback] = useState(null);
  const [modalMode, setModalMode] = useState(null);
  const [failureCopy, setFailureCopy] = useState('');
  const advanceTimerRef = useRef(null);

  const selectedSlug = managerUnlockSlug(selectedManager);
  const selectedUnlocked = Boolean(unlocked[selectedSlug]);
  const unlockedCount = Object.values(unlocked).filter(Boolean).length;
  const currentQuestion = deck.length ? deck[questionIndex % deck.length] : null;
  const progressPct = Math.min(100, (streak / GAUNTLET_TARGET_STREAK) * 100);
  const timerPct = Math.max(0, Math.min(100, (timeLeft / GAUNTLET_TIME_LIMIT_SECONDS) * 100));

  const categoryCount = useMemo(
    () => new Set(questionBank.map((question) => question.category)).size,
    [questionBank]
  );

  useEffect(() => {
    let cancelled = false;
    setLoadingQuestions(true);
    setLoadError('');

    loadKeeperGauntletQuestionBank()
      .then((questions) => {
        if (cancelled) return;
        setQuestionBank(questions);
      })
      .catch(() => {
        if (cancelled) return;
        setQuestionBank([]);
        setLoadError('The question bank could not load from the league archive.');
      })
      .finally(() => {
        if (!cancelled) setLoadingQuestions(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => () => {
    if (advanceTimerRef.current) window.clearTimeout(advanceTimerRef.current);
  }, []);

  useEffect(() => {
    if (!isRunning || answered || selectedUnlocked || !currentQuestion) return undefined;

    const startedAt = Date.now();
    setTimeLeft(GAUNTLET_TIME_LIMIT_SECONDS);

    const interval = window.setInterval(() => {
      const elapsedSeconds = Math.floor((Date.now() - startedAt) / 1000);
      const remaining = Math.max(0, GAUNTLET_TIME_LIMIT_SECONDS - elapsedSeconds);
      setTimeLeft(remaining);

      if (remaining <= 0) {
        window.clearInterval(interval);
        failRun('Time expired. The streak resets to zero.');
      }
    }, 250);

    return () => window.clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRunning, questionIndex, answered, selectedUnlocked, currentQuestion?.id]);

  function clearAdvanceTimer() {
    if (advanceTimerRef.current) {
      window.clearTimeout(advanceTimerRef.current);
      advanceTimerRef.current = null;
    }
  }

  function queueNextQuestion() {
    clearAdvanceTimer();
    advanceTimerRef.current = window.setTimeout(() => {
      setQuestionIndex((index) => index + 1);
      setAnswered(false);
      setSelectedChoice('');
      setFeedback(null);
    }, ADVANCE_DELAY_MS);
  }

  function startRun() {
    if (questionBank.length === 0) return;

    clearAdvanceTimer();
    const testSeed = typeof window !== 'undefined'
      ? window.localStorage.getItem('bruins09:keeper-vault:test-seed')
      : null;
    const seed = testSeed || `${selectedManager}:${Date.now()}:${Math.random()}`;
    setDeck(createKeeperGauntletDeck(questionBank, seed));
    setQuestionIndex(0);
    setStreak(0);
    setBestStreak(0);
    setTimeLeft(GAUNTLET_TIME_LIMIT_SECONDS);
    setAnswered(false);
    setSelectedChoice('');
    setFeedback(null);
    setFailureCopy('');
    setModalMode('quiz');
    setIsRunning(true);
  }

  function selectManager(manager) {
    stopRun();
    setSelectedManager(manager);
  }

  function stopRun() {
    clearAdvanceTimer();
    setIsRunning(false);
    setDeck([]);
    setQuestionIndex(0);
    setStreak(0);
    setBestStreak(0);
    setTimeLeft(GAUNTLET_TIME_LIMIT_SECONDS);
    setAnswered(false);
    setSelectedChoice('');
    setFeedback(null);
    setFailureCopy('');
    setModalMode(null);
  }

  function closeModal() {
    clearAdvanceTimer();
    setModalMode(null);
    if (modalMode === 'quiz') {
      setIsRunning(false);
      setDeck([]);
      setQuestionIndex(0);
      setStreak(0);
      setAnswered(false);
      setSelectedChoice('');
      setFeedback(null);
    }
  }

  function failRun(copy) {
    clearAdvanceTimer();
    setIsRunning(false);
    setAnswered(true);
    setSelectedChoice('');
    setStreak(0);
    setFailureCopy(copy);
    setFeedback(null);
    setModalMode('failure');
  }

  function unlockSelectedManager() {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(keeperVaultStorageKey(selectedSlug), 'unlocked');
    }

    setUnlocked((current) => ({ ...current, [selectedSlug]: true }));
    setIsRunning(false);
    setModalMode('success');
    setFeedback({
      type: 'success',
      title: 'Vault cleared.',
      copy: `${selectedManager}'s research docket is unlocked in this browser.`,
    });
  }

  function answerQuestion(choice) {
    if (!isRunning || answered || selectedUnlocked || !currentQuestion) return;

    setAnswered(true);
    setSelectedChoice(choice);

    if (choice === currentQuestion.answer) {
      const nextStreak = streak + 1;
      setStreak(nextStreak);
      setBestStreak((best) => Math.max(best, nextStreak));

      if (nextStreak >= GAUNTLET_TARGET_STREAK) {
        unlockSelectedManager();
        return;
      }

      setFeedback({
        type: 'correct',
        title: 'Correct.',
        copy: `${GAUNTLET_TARGET_STREAK - nextStreak} more in a row to open the dossier.`,
      });
      queueNextQuestion();
      return;
    }

    setStreak(0);
    failRun('Wrong answer. The streak resets to zero.');
  }

  return (
    <>
      <HeroBackground variant="gold" className="px-6 md:px-14 py-14 md:py-16">
        <Eyebrow>KEEPER VAULT</Eyebrow>
        <div className="mt-4 max-w-5xl">
          <h1
            className="font-display font-black uppercase leading-[0.9] tracking-normal text-ink-primary"
            style={{ fontSize: 'clamp(48px, 8vw, 104px)' }}
          >
            Three straight.<br />
            <span className="text-accent-gold">Or no dossier.</span>
          </h1>
          <p className="mt-6 max-w-3xl text-base md:text-xl text-ink-secondary leading-relaxed italic">
            The single-riddle lock is gone. The vault now pulls from the league archive and asks
            timed questions about first-round draft receipts, old team names, champions,
            rivalries, records, and headline weekly scores. Clear it and you earn access to
            manager-specific keeper research built for the real decision: who is worth carrying
            into next season at their draft-pick cost.
          </p>
        </div>

        <Reveal delay={80} className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
          {RULE_CARDS.map((card) => {
            const Icon = card.icon;
            return (
              <article key={card.label} className="bg-bg-deep/80 border border-border p-4 md:p-5">
                <Icon aria-hidden="true" className="w-5 h-5 text-accent-gold mb-4" />
                <div className="text-[9px] md:text-[10px] text-ink-tertiary tracking-[0.25em] font-extrabold uppercase">
                  {card.label}
                </div>
                <div className="mt-2 font-display text-2xl md:text-3xl font-black tracking-normal text-accent-gold uppercase">
                  {card.value}
                </div>
                <p className="mt-2 text-[12px] md:text-sm text-ink-secondary leading-snug italic">
                  {card.copy}
                </p>
              </article>
            );
          })}
        </Reveal>
      </HeroBackground>

      <section className="px-6 md:px-14 py-14 border-y border-border bg-bg-deep">
        <Reveal>
          <SectionHeader
            eyebrow="KEEPER GAUNTLET"
            title="Pick a manager. Survive the run."
            actionLabel="2025 DRAFT BOARD"
            actionHref="/draft-room/2025"
          />
        </Reveal>

        <Reveal delay={80} className="grid grid-cols-1 xl:grid-cols-[0.82fr_1.18fr] gap-6 md:gap-8">
          <ManagerSelector
            selectedManager={selectedManager}
            unlocked={unlocked}
            onSelect={selectManager}
          />

          <article className="bg-bg-surface border-t-[3px] border-accent-gold min-h-[620px]">
            <div className="p-6 md:p-8 border-b border-border">
              <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                <div className="flex items-center gap-4 min-w-0">
                  <ManagerAvatar name={selectedManager} size="lg" framed />
                  <div className="min-w-0">
                    <div className="text-[10px] tracking-[0.25em] text-ink-tertiary font-extrabold uppercase">
                      Active dossier
                    </div>
                    <h2 className="mt-1 font-display text-4xl md:text-5xl font-black tracking-normal uppercase leading-none">
                      {selectedManager}
                    </h2>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-[10px] tracking-[0.22em] font-extrabold uppercase text-accent-gold">
                  {selectedUnlocked ? (
                    <>
                      <Unlock aria-hidden="true" className="w-4 h-4" />
                      Unlocked
                    </>
                  ) : (
                    <>
                      <LockKeyhole aria-hidden="true" className="w-4 h-4" />
                      Locked
                    </>
                  )}
                </div>
              </div>

              <div className="mt-7 grid grid-cols-2 md:grid-cols-4 gap-3">
                <GauntletStat label="Pool" value={loadingQuestions ? 'Loading' : `${questionBank.length}`} detail="live questions" />
                <GauntletStat label="Categories" value={loadingQuestions ? '-' : `${categoryCount}`} detail="receipt lanes" />
                <GauntletStat label="Clock" value={`${GAUNTLET_TIME_LIMIT_SECONDS}s`} detail="per question" />
                <GauntletStat label="Target" value={`${GAUNTLET_TARGET_STREAK}`} detail="correct in a row" />
              </div>
            </div>

            {selectedUnlocked ? (
              <UnlockedDocket manager={selectedManager} />
            ) : (
              <GauntletStart
                loadingQuestions={loadingQuestions}
                loadError={loadError}
                questionCount={questionBank.length}
                onStart={startRun}
              />
            )}
          </article>
        </Reveal>
      </section>

      <KeeperGauntletModal
        mode={modalMode}
        manager={selectedManager}
        question={currentQuestion}
        streak={streak}
        bestStreak={bestStreak}
        progressPct={progressPct}
        timeLeft={timeLeft}
        timerPct={timerPct}
        answered={answered}
        selectedChoice={selectedChoice}
        feedback={feedback}
        failureCopy={failureCopy}
        onAnswer={answerQuestion}
        onStop={stopRun}
        onRetry={startRun}
        onClose={closeModal}
      />

      <section className="px-6 md:px-14 py-14">
        <Reveal className="grid grid-cols-1 lg:grid-cols-[0.9fr_1.1fr] gap-6 md:gap-8">
          <article className="bg-bg-surface p-6 md:p-8 border-t-[3px] border-accent-red">
            <Eyebrow color="red">RESEARCH STATUS</Eyebrow>
            <h2 className="mt-3 font-display text-4xl md:text-5xl font-black tracking-normal uppercase leading-none">
              Current boards are live.
            </h2>
            <p className="mt-4 text-sm md:text-base text-ink-secondary leading-relaxed italic">
              Authenticated Yahoo final rosters and 2025 draft costs now feed all ten Aug. 19
              market boards. The no-pick rule is confirmed; recommendations stay provisional
              until the Aug. 25 injury, depth-chart, and market review is complete.
            </p>
            <Link
              to={`/keeper-vault/research/${selectedSlug}`}
              className="mt-6 inline-flex items-center justify-center gap-2 text-[11px] text-accent-gold font-extrabold tracking-[0.25em] border border-accent-gold px-5 py-3 hover:bg-accent-gold hover:text-bg-deep transition-colors uppercase"
            >
              View current board
            </Link>
          </article>

          <article className="bg-bg-surface p-6 md:p-8 border-t-[3px] border-accent-gold">
            <div className="flex items-start justify-between gap-4">
              <div>
                <Eyebrow>VAULT PROGRESS</Eyebrow>
                <div className="mt-3 font-display text-4xl md:text-5xl font-black tracking-normal uppercase leading-none">
                  {unlockedCount} / {ACTIVE_MEMBERS.length} unlocked
                </div>
              </div>
              <BadgeCheck aria-hidden="true" className="w-8 h-8 text-accent-gold shrink-0" />
            </div>
            <p className="mt-4 text-sm md:text-base text-ink-secondary leading-relaxed italic">
              Unlock progress is saved in this browser. This is a hard game gate, not real security.
              True private dossiers should move answer validation and dossier delivery server-side.
            </p>
            <Link
              to="/draft-room"
              className="mt-6 inline-flex items-center justify-center gap-2 text-[11px] text-accent-gold font-extrabold tracking-[0.25em] border border-accent-gold px-5 py-3 hover:bg-accent-gold hover:text-bg-deep transition-colors uppercase"
            >
              Open draft room
            </Link>
          </article>
        </Reveal>
      </section>
    </>
  );
}

function KeeperGauntletModal({
  mode,
  manager,
  question,
  streak,
  bestStreak,
  progressPct,
  timeLeft,
  timerPct,
  answered,
  selectedChoice,
  feedback,
  failureCopy,
  onAnswer,
  onStop,
  onRetry,
  onClose,
}) {
  if (!mode) return null;

  const isQuiz = mode === 'quiz' && question;

  return (
    <div className="fixed inset-0 z-50 bg-bg-deep/95 backdrop-blur-md overflow-y-auto px-4 py-5 md:px-8 md:py-8">
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Keeper gauntlet"
        className="mx-auto max-w-6xl min-h-[calc(100vh-40px)] md:min-h-[calc(100vh-64px)] bg-bg-surface border border-accent-gold/60 shadow-2xl"
      >
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 border-b border-border p-5 md:p-6 bg-bg-deep">
          <div className="flex items-center gap-4 min-w-0">
            <ManagerAvatar name={manager} size="md" framed />
            <div className="min-w-0">
              <div className="text-[10px] tracking-[0.3em] text-accent-gold font-extrabold uppercase">
                Keeper gauntlet
              </div>
              <div className="mt-1 font-display text-3xl md:text-4xl font-black tracking-normal uppercase leading-none">
                {manager}
              </div>
            </div>
          </div>
          {isQuiz && (
            <button
              type="button"
              onClick={onStop}
              className="self-start md:self-auto inline-flex items-center justify-center gap-2 text-[11px] text-ink-secondary font-extrabold tracking-[0.2em] uppercase border border-border px-4 py-3 hover:border-accent-red hover:text-accent-red transition-colors"
            >
              <XCircle aria-hidden="true" className="w-4 h-4" />
              Exit gauntlet
            </button>
          )}
        </div>

        {isQuiz ? (
          <GauntletQuestion
            question={question}
            streak={streak}
            bestStreak={bestStreak}
            progressPct={progressPct}
            timeLeft={timeLeft}
            timerPct={timerPct}
            answered={answered}
            selectedChoice={selectedChoice}
            feedback={feedback}
            onAnswer={onAnswer}
            onStop={onStop}
          />
        ) : mode === 'failure' ? (
          <GauntletFailure copy={failureCopy} onRetry={onRetry} onClose={onClose} />
        ) : (
          <GauntletCelebration manager={manager} onClose={onClose} />
        )}
      </div>
    </div>
  );
}

function GauntletFailure({ copy, onRetry, onClose }) {
  return (
    <div className="min-h-[620px] flex items-center justify-center p-6 md:p-10">
      <div className="w-full max-w-3xl text-center">
        <div className="mx-auto w-32 h-32 md:w-44 md:h-44 rounded-full border-[6px] border-accent-red flex items-center justify-center bg-accent-red/10">
          <XCircle aria-hidden="true" className="w-24 h-24 md:w-32 md:h-32 text-accent-red" />
        </div>
        <div className="mt-8 text-[10px] tracking-[0.35em] text-accent-red font-extrabold uppercase">
          Streak broken
        </div>
        <h2
          className="mt-3 font-display font-black uppercase leading-[0.85] tracking-normal text-accent-red"
          style={{ fontSize: 'clamp(72px, 12vw, 150px)' }}
        >
          Try again.
        </h2>
        <p className="mt-5 mx-auto max-w-xl text-base md:text-xl text-ink-secondary leading-relaxed italic">
          {copy || 'The vault rejected the run. Reset and start over.'}
        </p>
        <div className="mt-8 flex flex-col sm:flex-row justify-center gap-3">
          <button
            type="button"
            onClick={onRetry}
            className="inline-flex min-h-[52px] items-center justify-center gap-2 bg-accent-gold text-bg-deep px-6 font-display text-sm font-black tracking-[0.2em] uppercase hover:bg-ink-primary transition-colors"
          >
            <RotateCcw aria-hidden="true" className="w-4 h-4" />
            Try again
          </button>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex min-h-[52px] items-center justify-center gap-2 border border-border px-6 text-[11px] text-ink-secondary font-extrabold tracking-[0.2em] uppercase hover:border-accent-red hover:text-accent-red transition-colors"
          >
            Leave vault
          </button>
        </div>
      </div>
    </div>
  );
}

function GauntletCelebration({ manager, onClose }) {
  return (
    <div className="min-h-[660px] relative overflow-hidden flex items-center justify-center p-6 md:p-10 bg-gold-radial">
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-gridiron pointer-events-none"
      />
      <div className="relative w-full max-w-4xl text-center">
        <div className="mx-auto w-32 h-32 md:w-44 md:h-44 rounded-full border-[6px] border-accent-gold flex items-center justify-center bg-accent-gold/15">
          <Trophy aria-hidden="true" className="w-24 h-24 md:w-32 md:h-32 text-accent-gold" />
        </div>
        <div className="mt-8 text-[10px] tracking-[0.35em] text-accent-gold font-extrabold uppercase">
          Three straight cleared
        </div>
        <h2
          className="mt-3 font-display font-black uppercase leading-[0.86] tracking-normal"
          style={{ fontSize: 'clamp(58px, 10vw, 126px)' }}
        >
          Research<br />
          <span className="text-accent-gold">unlocked.</span>
        </h2>
        <p className="mt-5 mx-auto max-w-2xl text-base md:text-xl text-ink-secondary leading-relaxed italic">
          {manager}'s keeper decision research is open. This is where eligible players get compared
          by keeper cost, 2026 projection, ADP value, role security, injury risk, and Bruins09
          scoring fit before a real keep-or-pass verdict is published.
        </p>
        <button
          type="button"
          onClick={onClose}
          className="mt-8 inline-flex min-h-[54px] items-center justify-center gap-2 bg-accent-gold text-bg-deep px-7 font-display text-sm font-black tracking-[0.2em] uppercase hover:bg-ink-primary transition-colors"
        >
          <Unlock aria-hidden="true" className="w-4 h-4" />
          View research
        </button>
      </div>
    </div>
  );
}

function ManagerSelector({ selectedManager, unlocked, onSelect }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-1 2xl:grid-cols-2 gap-3.5">
      {ACTIVE_MEMBERS.map((manager) => {
        const slug = managerUnlockSlug(manager);
        const isSelected = manager === selectedManager;
        const isUnlocked = Boolean(unlocked[slug]);
        const StatusIcon = isUnlocked ? Unlock : LockKeyhole;

        return (
          <button
            key={manager}
            type="button"
            aria-pressed={isSelected}
            onClick={() => onSelect(manager)}
            className={`text-left min-h-[110px] p-4 border transition-colors bg-bg-surface hover:bg-bg-elevated ${
              isSelected ? 'border-accent-gold' : 'border-border'
            }`}
          >
            <div className="flex items-start gap-3">
              <ManagerAvatar name={manager} size="sm" />
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-3">
                  <div className="font-display text-2xl font-black tracking-normal uppercase truncate">
                    {manager}
                  </div>
                  <StatusIcon
                    aria-hidden="true"
                    className={`w-4 h-4 shrink-0 ${isUnlocked ? 'text-accent-gold' : 'text-ink-tertiary'}`}
                  />
                </div>
                <div className="mt-1 text-[9px] tracking-[0.22em] text-accent-gold font-extrabold uppercase">
                  {isUnlocked ? 'Dossier open' : 'Gauntlet locked'}
                </div>
                <p className="mt-2 text-sm text-ink-secondary leading-snug italic">
                  {isUnlocked ? 'Research lanes available.' : `${GAUNTLET_TARGET_STREAK} correct answers required.`}
                </p>
              </div>
            </div>
          </button>
        );
      })}
    </div>
  );
}

function GauntletStat({ label, value, detail }) {
  return (
    <div className="bg-bg-deep border border-border p-4">
      <div className="text-[9px] tracking-[0.25em] text-ink-tertiary font-extrabold uppercase">
        {label}
      </div>
      <div className="mt-2 font-display text-2xl md:text-3xl font-black tracking-normal text-accent-gold uppercase">
        {value}
      </div>
      <div className="mt-1 text-[11px] text-ink-tertiary italic">
        {detail}
      </div>
    </div>
  );
}

function GauntletStart({ loadingQuestions, loadError, questionCount, onStart }) {
  const canStart = !loadingQuestions && questionCount >= 100;

  return (
    <div className="p-6 md:p-8">
      <div className="bg-bg-deep border-l-[3px] border-accent-gold p-5 md:p-6">
        <div className="text-[10px] tracking-[0.25em] text-accent-gold font-extrabold uppercase">
          Rules of entry
        </div>
        <h3 className="mt-3 font-display text-4xl md:text-5xl font-black tracking-normal uppercase leading-none">
          Three in a row. One miss resets everything.
        </h3>
        <p className="mt-4 text-sm md:text-base text-ink-secondary leading-relaxed italic">
          Questions are pulled from real Bruins09 files. The pool is now curated for stuff the
          league might actually remember: first-round draft picks, champions, old team names,
          standings receipts, rivalry leaders, and season-high weekly scores.
        </p>
        <div className="mt-5 border-l-2 border-accent-gold pl-4 text-sm md:text-base text-ink-tertiary leading-relaxed">
          Beat the quiz to unlock the keeper decision room: a provisional Aug. 19 board built from
          authenticated Yahoo rosters, original draft costs, and a current superflex market.
        </div>
        {loadError && (
          <p className="mt-4 text-sm text-accent-red italic" role="alert">
            {loadError}
          </p>
        )}
        <button
          type="button"
          disabled={!canStart}
          onClick={onStart}
          className="mt-6 inline-flex min-h-[48px] items-center justify-center gap-2 bg-accent-gold text-bg-deep px-5 font-display text-sm font-black tracking-[0.2em] uppercase hover:bg-ink-primary transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Trophy aria-hidden="true" className="w-4 h-4" />
          {loadingQuestions ? 'Loading archive' : 'Begin gauntlet'}
        </button>
      </div>

      <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-4">
        <InstructionCard icon={Clock3} title="Timed pressure" copy={`${GAUNTLET_TIME_LIMIT_SECONDS} seconds per question. Timeout equals a miss.`} />
        <InstructionCard icon={RotateCcw} title="Streak reset" copy="Wrong answers reset the run to zero and do not reveal the correct answer." />
        <InstructionCard icon={Database} title="Curated pool" copy={`${questionCount || '200-ish'} archive questions from first-round picks, standings, scores, and rivalries.`} />
      </div>
    </div>
  );
}

function InstructionCard({ icon: Icon, title, copy }) {
  return (
    <article className="bg-bg-deep p-5 border border-border">
      <Icon aria-hidden="true" className="w-5 h-5 text-accent-gold mb-4" />
      <div className="font-display text-2xl font-black tracking-normal uppercase">
        {title}
      </div>
      <p className="mt-2 text-sm text-ink-tertiary leading-relaxed italic">
        {copy}
      </p>
    </article>
  );
}

function GauntletQuestion({
  question,
  streak,
  bestStreak,
  progressPct,
  timeLeft,
  timerPct,
  answered,
  selectedChoice,
  feedback,
  onAnswer,
  onStop,
}) {
  return (
    <div className="p-6 md:p-8">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
        <div className="grid grid-cols-3 gap-3 w-full lg:max-w-[520px]">
          <RunMeter label="Streak" value={`${streak}/${GAUNTLET_TARGET_STREAK}`} />
          <RunMeter label="Best" value={bestStreak} />
          <RunMeter label="Clock" value={`${timeLeft}s`} danger={timeLeft <= 5} />
        </div>
        <button
          type="button"
          onClick={onStop}
          className="self-start lg:self-auto inline-flex items-center justify-center gap-2 text-[11px] text-ink-secondary font-extrabold tracking-[0.2em] uppercase border border-border px-4 py-3 hover:border-accent-red hover:text-accent-red transition-colors"
        >
          <XCircle aria-hidden="true" className="w-4 h-4" />
          End run
        </button>
      </div>

      <div className="h-2 bg-bg-deep border border-border mb-3">
        <div className="h-full bg-accent-gold transition-all" style={{ width: `${progressPct}%` }} />
      </div>
      <div className="h-1 bg-bg-deep mb-7">
        <div
          className={`h-full transition-all ${timeLeft <= 5 ? 'bg-accent-red' : 'bg-ink-tertiary'}`}
          style={{ width: `${timerPct}%` }}
        />
      </div>

      <div className="bg-bg-deep border-l-[3px] border-accent-gold p-5 md:p-6">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-[10px] tracking-[0.22em] font-extrabold uppercase">
          <span className="text-accent-gold">{question.category}</span>
          <span className={difficultyClass(question.difficulty)}>{question.difficulty}</span>
          <span className="text-ink-tertiary">{question.sourceLabel}</span>
        </div>
        <h3 className="mt-4 font-display text-3xl md:text-5xl font-black tracking-normal uppercase leading-[0.96]">
          {question.prompt}
        </h3>
      </div>

      <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-3.5">
        {question.choices.map((choice) => {
          const wasSelected = selectedChoice === choice;
          const isCorrectSelection = wasSelected && choice === question.answer;
          const isWrongSelection = wasSelected && choice !== question.answer;
          const stateClass = isCorrectSelection
            ? 'border-accent-gold text-accent-gold'
            : isWrongSelection
              ? 'border-accent-red text-accent-red'
              : 'border-border text-ink-primary hover:border-accent-gold hover:text-accent-gold';

          return (
            <button
              key={choice}
              type="button"
              disabled={answered}
              onClick={() => onAnswer(choice)}
              className={`min-h-[76px] bg-bg-deep border p-4 text-left font-display text-2xl font-black tracking-normal uppercase transition-colors disabled:cursor-default ${stateClass}`}
            >
              {choice}
            </button>
          );
        })}
      </div>

      {feedback && (
        <div
          className={`mt-5 p-4 border-l-[3px] bg-bg-deep ${
            feedback.type === 'correct' || feedback.type === 'success' ? 'border-accent-gold' : 'border-accent-red'
          }`}
          role="status"
        >
          <div className="flex items-start gap-3">
            {feedback.type === 'correct' || feedback.type === 'success' ? (
              <CheckCircle2 aria-hidden="true" className="w-5 h-5 text-accent-gold shrink-0 mt-0.5" />
            ) : (
              <XCircle aria-hidden="true" className="w-5 h-5 text-accent-red shrink-0 mt-0.5" />
            )}
            <div>
              <div className="text-[10px] tracking-[0.25em] text-ink-tertiary font-extrabold uppercase">
                {feedback.title}
              </div>
              <p className="mt-1 text-sm text-ink-secondary leading-relaxed italic">
                {feedback.copy}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function RunMeter({ label, value, danger = false }) {
  return (
    <div className="bg-bg-deep border border-border p-3.5">
      <div className="text-[9px] tracking-[0.25em] text-ink-tertiary font-extrabold uppercase">
        {label}
      </div>
      <div className={`mt-1 font-display text-2xl md:text-3xl font-black tracking-normal uppercase ${danger ? 'text-accent-red' : 'text-accent-gold'}`}>
        {value}
      </div>
    </div>
  );
}

function UnlockedDocket({ manager }) {
  const slug = managerSlug(manager);
  const dossier = getKeeperDossier(slug);
  const hasRealDossier = Boolean(dossier);

  return (
    <div className="p-6 md:p-8">
      <div className="bg-bg-deep border-l-[3px] border-accent-gold p-5 md:p-6 mb-5">
        <div className="text-[10px] tracking-[0.25em] text-accent-gold font-extrabold uppercase">
          {manager} dossier {hasRealDossier ? 'published' : 'unlocked'}
        </div>
        {hasRealDossier ? (
          <>
            <p className="mt-3 text-sm md:text-base text-ink-secondary leading-relaxed italic">
              The Aug. 19 provisional board is live from the authenticated Yahoo final roster,
              original 2025 draft costs, the confirmed no-pick rule for undrafted players, and a
              current superflex market. The final news and market pass is scheduled for Aug. 25.
            </p>
            <Link
              to={`/keeper-vault/research/${slug}`}
              className="mt-5 inline-flex items-center justify-center gap-2 bg-accent-gold text-bg-deep px-5 py-3 font-display text-sm font-black tracking-[0.2em] uppercase hover:bg-ink-primary transition-colors"
            >
              <Unlock aria-hidden="true" className="w-4 h-4" />
              View {manager}'s dossier
            </Link>
          </>
        ) : (
          <>
            <p className="mt-3 text-sm md:text-base text-ink-secondary leading-relaxed italic">
              The gauntlet is cleared. {manager}'s current provisional dossier is available.
            </p>
            <Link
              to={`/keeper-vault/research/${slug}`}
              className="mt-5 inline-flex items-center justify-center gap-2 border border-accent-gold px-4 py-3 text-[11px] font-extrabold tracking-[0.22em] text-accent-gold uppercase hover:bg-accent-gold hover:text-bg-deep transition-colors"
            >
              View current dossier
            </Link>
          </>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {RESEARCH_STEPS.map((step) => {
          const Icon = step.icon;
          return (
            <div key={step.label} className="bg-bg-deep p-5 border border-border">
              <Icon aria-hidden="true" className="w-5 h-5 text-accent-gold mb-4" />
              <div className="text-[10px] tracking-[0.25em] text-accent-gold font-extrabold uppercase">
                Research lane
              </div>
              <div className="mt-2 font-display text-2xl font-black tracking-normal uppercase">
                {step.label}
              </div>
              <p className="mt-2 text-sm text-ink-secondary leading-relaxed italic">
                {step.copy}
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );
}
